import { useEffect, useRef, useCallback } from 'react';

interface UseInfiniteScrollOptions {
  /** Whether there are more items to load */
  hasMore: boolean;
  /** Whether currently loading */
  isLoading: boolean;
  /** Function to call to load more items */
  onLoadMore: () => void;
  /** Distance from bottom (in pixels) to trigger load. Default: 200 */
  threshold?: number;
}

/**
 * Hook for infinite scroll functionality.
 * Returns a ref to attach to the scrollable container.
 */
export function useInfiniteScroll<T extends HTMLElement = HTMLDivElement>({
  hasMore,
  isLoading,
  onLoadMore,
  threshold = 200,
}: UseInfiniteScrollOptions) {
  const containerRef = useRef<T>(null);
  const loadMoreRef = useRef(onLoadMore);

  // Keep loadMore ref current
  useEffect(() => {
    loadMoreRef.current = onLoadMore;
  }, [onLoadMore]);

  const handleScroll = useCallback(() => {
    const container = containerRef.current;
    if (!container || isLoading || !hasMore) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

    if (distanceFromBottom < threshold) {
      loadMoreRef.current();
    }
  }, [hasMore, isLoading, threshold]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.addEventListener('scroll', handleScroll);
    // Check on mount in case content doesn't fill the container
    handleScroll();

    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  return containerRef;
}

/**
 * Loading indicator component for infinite scroll
 */
export function LoadingMore({ isLoading }: { isLoading: boolean }) {
  if (!isLoading) return null;
  return (
    <div className="py-4 text-center text-gray-500 dark:text-gray-400 text-sm">
      Loading more...
    </div>
  );
}

/**
 * End of list indicator
 */
export function EndOfList({ show, itemCount }: { show: boolean; itemCount: number }) {
  if (!show || itemCount === 0) return null;
  return (
    <div className="py-4 text-center text-gray-400 dark:text-gray-500 text-sm">
      End of list
    </div>
  );
}
